import React, {useCallback, useMemo} from 'react';

import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SectionWrapper} from 'metadata/MetadataEditorInfoSection';
import AnalysisTemplateConfigContext, {
  AnalysisTemplateConfigContextValue,
} from 'procedures/design/analysis-template-config/AnalysisTemplateConfigContext';
import {
  Method,
  ProcedureAnalysisTemplateConfiguration,
  isInaccessibleAnalysisTemplateConfiguration,
} from 'procedures/ProcedureDomainTypes';
import {AnalysisTemplatesQuery} from 'procedures/shared-components/analysis/__generated/AnalysisTemplatesQuery';
import {NotUsedInAnalysis} from 'procedures/shared-components/analysis/DatasetRendering';
import {ANALYSIS_TEMPLATES_QUERY} from 'procedures/shared-components/analysis/useAnalyzePreconfiguredAssayState';
import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import runModal from 'ui-library/runModal';

import {AnalysisSourcesOrigin} from '../AssayProcedureMethodAnalysisTypes';
import AnalysisTemplateConfigModal from './AnalysisTemplateConfigModal';
import {makeEmptyAnalysisTemplateMappings, HydratedTemplateConfigData} from './AnalysisTemplateConfigTypes';
import AnalysisTemplateSourceTable from './AnalysisTemplateSourceTable';
import AnalysisTemplateTable from './AnalysisTemplateTable';
import {useAnalysisSourcesDuringConfig} from './useAnalysisSources';

export interface AnalysisTemplateConfigProps {
  method: Method;
  onChangeMethod: (method: Method) => void;
  isProcedureReadOnly: boolean;
  navigateToTests: () => void;
}

function isTemplatePresent(
  analysisTemplateConfiguration: Array<ProcedureAnalysisTemplateConfiguration>,
  targetTemplateId: string
): boolean {
  return (
    analysisTemplateConfiguration.find((template) => template.analysisTemplateId === targetTemplateId) != null
  );
}

/**
 * Builds an indicator to show which of the already-configured analysis templates a dataset is used in
 *
 * @package Exported for testing
 * */
export function usedInWhichAnalysisTemplatesHelper(
  datasetId: string,
  hydratedAnalysisTemplateConfigurations: Array<HydratedTemplateConfigData>
): Array<string> | null {
  const whichTemplates = new Set<string>();
  for (const templateConfig of hydratedAnalysisTemplateConfigurations) {
    for (const tableMapping of templateConfig.config.tableMappings) {
      if (tableMapping.datasetId === datasetId) {
        whichTemplates.add(templateConfig.template.name);
      }
    }
  }
  return whichTemplates.size === 0 ? null : [...whichTemplates];
}

function usedInWhichAnalysisTemplates(
  datasetId: string,
  hydratedAnalysisTemplateConfigurations: Array<HydratedTemplateConfigData>
): JSX.Element {
  const whichTemplates = usedInWhichAnalysisTemplatesHelper(
    datasetId,
    hydratedAnalysisTemplateConfigurations
  );
  return whichTemplates == null ? <NotUsedInAnalysis /> : <p>{[...whichTemplates].join(', ')}</p>;
}

export default function AnalysisTemplateConfig(props: AnalysisTemplateConfigProps): JSX.Element | null {
  const {method, onChangeMethod, isProcedureReadOnly, navigateToTests} = props;
  const {id: methodId, analysisTemplateConfigurations} = method;
  const analysisSources = useAnalysisSourcesDuringConfig(methodId);
  const configuredProcedureAnalysisTemplates: Array<ProcedureAnalysisTemplateConfiguration> = useMemo(() => {
    const accessibleConfigs = analysisTemplateConfigurations.filter(
      (oneConfig): oneConfig is ProcedureAnalysisTemplateConfiguration =>
        !isInaccessibleAnalysisTemplateConfiguration(oneConfig)
    );
    // Note that if any configs are inaccessible, the entire procedure is turned read-only. I.e., this filter
    // won't cause data loss
    assert(
      accessibleConfigs.length === analysisTemplateConfigurations.length || isProcedureReadOnly,
      'Should not be able to edit template configs on a read-only assay'
    );
    return accessibleConfigs;
  }, [analysisTemplateConfigurations, isProcedureReadOnly]);

  const queryResult = useBenchlingQuery<AnalysisTemplatesQuery>(ANALYSIS_TEMPLATES_QUERY, {
    variables: {
      ids: configuredProcedureAnalysisTemplates.map(({analysisTemplateId}) => analysisTemplateId),
    },
  });

  const hydratedAnalysisTemplateConfigurations = useMemo((): Array<HydratedTemplateConfigData> => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return [];
    }
    return queryResult.data.analysisTemplates
      .filter((template): template is AnalysisTemplateGraphQL => !isInaccessible(template))
      .map((template) => ({
        template,
        config: configuredProcedureAnalysisTemplates.find(
          (configuredTemplate) => configuredTemplate.analysisTemplateId === template.id
        )?.config ?? {tableMappings: []},
      }));
  }, [queryResult, configuredProcedureAnalysisTemplates]);

  const contextValue: AnalysisTemplateConfigContextValue = useMemo(
    () => ({
      usedInWhichAnalysisTemplates: (datasetId) =>
        usedInWhichAnalysisTemplates(datasetId, hydratedAnalysisTemplateConfigurations),
    }),
    [hydratedAnalysisTemplateConfigurations]
  );

  const addOrEditTemplateConfiguration = useCallback(
    async (selectedTemplate: AnalysisTemplateGraphQL) => {
      const configIndex = configuredProcedureAnalysisTemplates.findIndex(
        (oneConfig) => selectedTemplate.id === oneConfig.analysisTemplateId
      );
      const isEditing = configIndex >= 0;

      const initialConfiguration = isEditing
        ? configuredProcedureAnalysisTemplates[configIndex]
        : {
            analysisTemplateId: selectedTemplate.id,
            config: makeEmptyAnalysisTemplateMappings(),
          };

      const newConfig = await runModal(AnalysisTemplateConfigModal, {
        analysisTemplate: selectedTemplate,
        // This function can only be called with analysisSources != null
        availableSources: analysisSources!,
        // The modal does not have direct access to context, so we pass it in as a prop
        analysisTemplateConfigContext: contextValue,
        initialConfiguration,
        modalTitle: isEditing ? 'Edit analysis template' : 'Add analysis template',
        submitButtonText: isEditing ? 'Save changes' : 'Add analysis template',
      });

      if (newConfig != null) {
        const newConfigurations = [...configuredProcedureAnalysisTemplates];
        if (configIndex < 0) {
          newConfigurations.push(newConfig);
        } else {
          newConfigurations[configIndex] = newConfig;
        }
        onChangeMethod({
          ...method,
          analysisTemplateConfigurations: newConfigurations,
        });
      }
    },
    [analysisSources, configuredProcedureAnalysisTemplates, contextValue, method, onChangeMethod]
  );

  const removeTemplateConfiguration = (templateId: string): void => {
    onChangeMethod({
      ...method,
      analysisTemplateConfigurations: configuredProcedureAnalysisTemplates.filter(
        (configuredTemplate) => configuredTemplate.analysisTemplateId !== templateId
      ),
    });
  };

  if (queryResult.type === QueryResultType.ERROR) {
    return null;
  } else if (queryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <div {...getTestProps({component: 'AnalysisTemplateConfig', element: 'root'})}>
      <AnalysisTemplateConfigContext.Provider value={contextValue}>
        <SectionWrapper headerName="Select and map data to analysis templates">
          <p>Select an analysis template to use on data captured during execution.</p>
          <AnalysisTemplateTable
            templateConfigs={hydratedAnalysisTemplateConfigurations}
            sources={analysisSources}
            onSelectTemplate={addOrEditTemplateConfiguration}
            onRemoveTemplate={removeTemplateConfiguration}
            filterTemplate={(template: AnalysisTemplateGraphQL) =>
              !isTemplatePresent(configuredProcedureAnalysisTemplates, template.id)
            }
            disabled={isProcedureReadOnly || analysisSources == null}
          />
        </SectionWrapper>
        <SectionWrapper headerName="View unit operation data">
          <p>Based on your current configuration, here's the data that will be captured during execution.</p>
          <AnalysisTemplateSourceTable
            sources={analysisSources}
            navigateToTests={navigateToTests}
            analysisSourcesOrigin={AnalysisSourcesOrigin.CONFIGURATION}
          />
        </SectionWrapper>
      </AnalysisTemplateConfigContext.Provider>
    </div>
  );
}
